import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { initializeFirebase, db } from '@/lib/firebase';
import {
  signIn as authSignIn,
  signUp as authSignUp,
  signOut as authSignOut,
  signInWithGoogle as authSignInWithGoogle,
  getUserProfile,
  updateUserProfile,
} from '@/lib/auth';
import type { UserProfile, AuthContextType } from '@/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;
    let unsubscribeProfile: (() => void) | undefined;
  
    const setupAuth = async () => {
      try {
        const { auth } = await initializeFirebase();
        
        unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser: User | null) => {
          if (firebaseUser) {
            // Firestore のユーザープロフィールをリアルタイム監視
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            
            unsubscribeProfile = onSnapshot(
              userDocRef,
              (docSnapshot) => {
                if (docSnapshot.exists()) {
                  const profile = {
                    uid: firebaseUser.uid,
                    ...docSnapshot.data(),
                  } as UserProfile;
                  
                  setUser(profile);
                } else {
                  // プロフィールが存在しない場合は従来の方法で取得
                  getUserProfile(firebaseUser.uid).then((profile) => {
                    if (profile && !profile.uid) {
                      profile.uid = firebaseUser.uid;
                    }
                    setUser(profile);
                  });
                }
                setLoading(false);
              },
              (error) => {
                console.error('Failed to subscribe to user profile:', error);
                setLoading(false);
              }
            );
          } else {
            setUser(null);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error('Failed to setup auth:', error);
        setLoading(false);
      }
    };
  
    setupAuth();
  
    return () => {
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);  

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const firebaseUser = await authSignIn(email, password);
      const profile = await getUserProfile(firebaseUser.uid);
      
      // uid を確実に設定
      if (profile && !profile.uid) {
        profile.uid = firebaseUser.uid;
      }
      
      setUser(profile);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    setLoading(true);
    try {
      const firebaseUser = await authSignUp(email, password, displayName);
      const profile = await getUserProfile(firebaseUser.uid);
      
      // uid を確実に設定
      if (profile && !profile.uid) {
        profile.uid = firebaseUser.uid;
      }
      
      setUser(profile);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await authSignOut();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const firebaseUser = await authSignInWithGoogle();
      
      // プロフィールを取得（存在しない場合は作成済み）
      let profile = await getUserProfile(firebaseUser.uid);
      
      // プロフィールが取得できない場合は少し待ってリトライ
      if (!profile) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        profile = await getUserProfile(firebaseUser.uid);
      }
      
      // uid を確実に設定
      if (profile && !profile.uid) {
        profile.uid = firebaseUser.uid;
      }
      
      setUser(profile);
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) throw new Error('No user logged in');
    await updateUserProfile(user.uid, data);
    setUser({ ...user, ...data });
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
